import { Component } from 'react';
import I18n from 'i18n-js';
import ResultBox, { ResultBoxProps } from '@App/components/search/ResultBox';
import ResultTabsContent from '@App/components/search/ResultTabsContent';

interface State {
  tab: string | null
  userSelected: boolean
}

export default class ResultTabs extends Component<ResultBoxProps, State> {
  static propTypes = {
    ...ResultBox.propTypes,
  };

  static defaultProps = {
    ...ResultBox.defaultProps,
  };

  static TAB_TAGS = 'tags';
  static TAB_TRACKS = 'tracks';
  static TAB_ALBUMS= 'albums';
  static TAB_PLAYLISTS = 'playlists';
  static TAB_SOUND_EFFECTS = 'soundEffects';

  constructor(props: ResultBoxProps) {
    super(props);

    this.state = {
      tab: (this.constructor as typeof ResultTabs).getDefaultTab(props),
      userSelected: false,
    };

    this.onClick = this.onClick.bind(this);
  }

  static getDefaultTab(props: ResultBoxProps) {
    if (props.loading) {
      return null;
    }

    const { tags, tracks, albums, playlists, soundEffects } = props;
    const { TAB_TAGS, TAB_TRACKS, TAB_ALBUMS, TAB_PLAYLISTS, TAB_SOUND_EFFECTS } = this;

    if (tags!.count > 0) {
      return TAB_TAGS;
    }

    if (tracks!.count > 0) {
      return TAB_TRACKS;
    }

    if (albums!.count > 0) {
      return TAB_ALBUMS;
    }

    if (playlists!.count > 0) {
      return TAB_PLAYLISTS;
    }

    if (soundEffects && soundEffects.count > 0) {
      return TAB_SOUND_EFFECTS;
    }

    return TAB_TAGS;
  }

  componentDidUpdate(prevProps: ResultBoxProps) {
    const Klass = this.constructor as typeof ResultTabs;

    // A fresh music (Algolia) query just resolved → (re)pick the default tab for the new search
    // and clear any manual selection carried over from the previous search.
    if (prevProps.loading && !this.props.loading) {
      this.setState({ tab: Klass.getDefaultTab(this.props), userSelected: false });

      return;
    }

    // The "Effets sonores" column can be toggled off (soundEffects -> null) while its tab is active;
    // fall back to the default tab so renderContent() never dereferences null.
    if (this.state.tab === Klass.TAB_SOUND_EFFECTS && !this.props.soundEffects) {
      this.setState({ tab: Klass.getDefaultTab(this.props), userSelected: false });

      return;
    }

    // The sound-effects query resolves *independently* from the music query: the default tab
    // picked above only knew the (then-empty) music counts. When PSE results arrive later, an
    // empty tab can stay active while "Effets sonores (N)" advertises matches. If the user has not
    // manually chosen a tab and the active one is empty, promote the freshly computed default
    // (which now sees the sound-effect count).
    if (!this.props.loading && !this.state.userSelected && this.isCurrentTabEmpty()) {
      const nextTab = Klass.getDefaultTab(this.props);

      if (nextTab !== this.state.tab) {
        this.setState({ tab: nextTab });
      }
    }
  }

  isCurrentTabEmpty(): boolean {
    const { tab } = this.state;

    if (tab === null) {
      return true;
    }

    // TAB_* values match the section prop names (tags, tracks, albums, playlists, soundEffects).
    const section = (this.props as unknown as Record<string, { count: number } | null | undefined>)[tab];

    return !section || section.count < 1;
  }

  onClick(event) {
    this.setState({ tab: event.currentTarget.value, userSelected: true });
  }

  renderTabButton(tab: string, search: { count: number }, label: string) {
    if (this.props.loading) {
      return null;
    }

    const { count } = search;

    if (count < 1) {
      return null;
    }

    const active = this.state.tab === tab;

    return (
      <button value={tab} type="button" className={`tabs__item ${active ? 'active' : ''}`} onClick={this.onClick}>
        <span>{label} ({count})</span>
      </button>
    );
  }

  renderContent() {
    if (this.props.loading) {
      return null;
    }

    const { TAB_TAGS, TAB_TRACKS, TAB_ALBUMS, TAB_PLAYLISTS, TAB_SOUND_EFFECTS } = this.constructor as typeof ResultTabs;
    const { tags, tracks, albums, playlists, soundEffects, onSearch, onFind, onSubmit, onSoundEffects } = this.props;
    const { renderTag, renderTrack, renderAlbum, renderPlaylist, renderSoundEffect } = ResultBox;
    const { tab } = this.state;

    switch(tab) {
      case TAB_TAGS:
        return <ResultTabsContent>{tags!.list.map(item => renderTag(item, onSearch))}</ResultTabsContent>;

      case TAB_TRACKS:
        return <ResultTabsContent onSeeAll={onSubmit}>{tracks!.list.map(item => renderTrack(item, onFind))}</ResultTabsContent>;

      case TAB_ALBUMS:
        return <ResultTabsContent>{albums!.list.map(item => renderAlbum(item, onFind))}</ResultTabsContent>;

      case TAB_PLAYLISTS:
        return <ResultTabsContent>{playlists!.list.map(item => renderPlaylist(item, onFind))}</ResultTabsContent>;

      case TAB_SOUND_EFFECTS:
        return soundEffects
          ? <ResultTabsContent onSeeAll={onSoundEffects}>{soundEffects.list.map(item => renderSoundEffect(item, onSoundEffects!))}</ResultTabsContent>
          : null;

      default:
        return null;
    }
  }

  render() {
    const { TAB_TAGS, TAB_TRACKS, TAB_ALBUMS, TAB_PLAYLISTS, TAB_SOUND_EFFECTS } = this.constructor as typeof ResultTabs;
    const { tags, tracks, albums, playlists, soundEffects } = this.props;

    return (
      <div className="result-tabs">
        <div className="tabs">
          {this.renderTabButton(TAB_TAGS, tags!, I18n.t('search.results.tags'))}
          {this.renderTabButton(TAB_TRACKS, tracks!, I18n.t('search.results.tracks'))}
          {this.renderTabButton(TAB_ALBUMS, albums!, I18n.t('search.results.albums'))}
          {this.renderTabButton(TAB_PLAYLISTS, playlists!, I18n.t('search.results.playlists'))}
          {soundEffects ? this.renderTabButton(TAB_SOUND_EFFECTS, soundEffects, I18n.t('search.results.soundEffects')) : null}
        </div>
        <div className="tabs-content">
          {this.renderContent()}
        </div>
      </div>
    );
  }
}
